/**
 * Die Gliederung des Gewerks Sanitär — an EINER Stelle (TX-STANDARD S1.1:
 * „Gliederung über seiten/<gewerk>/abschnitte.ts“). Inhalt aus dem
 * TS9-Grundgerüst (Commit c8152c07, 17.10.2026), im Muster der Lüftung (TV5)
 * übernommen mit TS9b (TD3, 26.10.2026).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * DREIZEHN SEITEN, VIER ZWISCHENZEILEN — UND KEINE NEUE ROUTE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bis TS9b standen die dreizehn Sanitär-Unterrouten als feste Zeilen in
 * `navigation/struktur.ts` (seit S1, 20.09.2026: aus fünf internen Reitern
 * wurden echte Routen). Diese Datei übernimmt sie UNVERÄNDERT:
 *
 *   - dieselben Pfade (die in App.tsx registrierten Routen),
 *   - dieselben Beschriftungen und Sinnbilder,
 *   - dieselben Schlüssel (`sanitaer-<seite>`; Befehlspalette, Favoriten und
 *     `Sidebar.test.tsx` kennen sie) - deshalb wie bei der Lüftung je Eintrag
 *     in der Datei und nicht aus dem Pfad gebildet,
 *   - dieselbe Reihenfolge und dieselben vier Zwischenzeilen (`art` wie
 *     bisher: planung, berechnung, daten, pruefung).
 *
 * Neu ist nur die Erläuterung je Seite (`hinweis`, aus dem Grundgerüst).
 * Neue Seiten des Paketplans (Überflutung, Druckzonen, Absicherung, …) kommen
 * erst hierher, wenn ihre Route existiert - Navigation verweist nur auf real
 * Vorhandenes (Muster N1, Punkt 6).
 *
 * Die drei Abwasserrechnungen (Schmutzwasser, Fallleitungen, Hebeanlagen)
 * bleiben getrennt: Sie schneiden nach EMPFÄNGER (Sanitärplaner,
 * Rohbauplaner, Anlagenbauer), nicht nach Anzeige.
 *
 * Die Übersicht (`sanitaer`, Index) steht unter „Daten“ und fällt in
 * `alleEintraege` automatisch heraus (Kind == Elternpfad) — sie dokumentiert
 * nur den vollständigen Satz (Muster N1, Punkt 3).
 */

/** Das Sinnbild eines Eintrags; `struktur.ts` übersetzt es in die Komponente aus `Icons.tsx`. */
export type SanitaerSinnbild = 'sanitaer' | 'berechnung' | 'tabelle' | 'raum' | 'datei' | 'warnung';

export interface SanitaerEintrag {
  /** Stabiler Schlüssel (Favoriten, Tests) — unverändert gegenüber den bisherigen festen Zeilen. */
  schluessel: string;
  /** Der Pfad UNTER /projekte/:id/ — genau wie in App.tsx. */
  pfad: string;
  name: string;
  sinnbild: SanitaerSinnbild;
  /** Wozu die Seite da ist. Erscheint als Erläuterung in Baum und Befehlspalette. */
  hinweis: string;
}

export interface SanitaerAbschnitt {
  /** Die Zwischenzeile im Baum: eine der vier Arten aus `ART_BESCHRIFTUNG`. */
  art: 'planung' | 'berechnung' | 'daten' | 'pruefung';
  eintraege: SanitaerEintrag[];
}

export const SANITAER_ABSCHNITTE: SanitaerAbschnitt[] = [
  {
    art: 'planung',
    eintraege: [
      {
        schluessel: 'sanitaer-anlagen', pfad: 'sanitaer/anlagen', name: 'Anlagen', sinnbild: 'sanitaer',
        hinweis: 'Hebeanlagen, Abscheider, Druckerhöhung und Erwärmer — Bestand und Auslegung je Anlage',
      },
    ],
  },
  {
    art: 'berechnung',
    eintraege: [
      {
        schluessel: 'sanitaer-trinkwasser', pfad: 'sanitaer/trinkwasser', name: 'Trinkwasser', sinnbild: 'berechnung',
        hinweis: 'DIN 1988-300: Spitzendurchfluss, Stränge, Druckerhöhung und Druckzonen',
      },
      {
        // TS9b2: eine eigene Seite aus dem Trinkwasserlauf - Zone, Anlage und Druckminderer sind Bestellangaben.
        schluessel: 'sanitaer-druckzonen', pfad: 'sanitaer/druckzonen', name: 'Druckzonen', sinnbild: 'berechnung',
        hinweis: 'Druckzonen, Druckerhöhungsanlagen und Druckminderer aus der Trinkwasserberechnung (DIN 1988-200/-300)',
      },
      {
        schluessel: 'sanitaer-warmwasser', pfad: 'sanitaer/warmwasser', name: 'Warmwasser', sinnbild: 'berechnung',
        hinweis: 'Frischwasserstationen, Durchlauferhitzer, Zirkulation und DVGW W 551',
      },
      {
        schluessel: 'sanitaer-schmutzwasser', pfad: 'sanitaer/schmutzwasser', name: 'Schmutzwasser', sinnbild: 'berechnung',
        hinweis: 'DIN EN 12056-2 / DIN 1986-100: Netzteile und Teilfüllung der Sammelleitungen',
      },
      {
        schluessel: 'sanitaer-fallleitungen', pfad: 'sanitaer/fallleitungen', name: 'Fallleitungen', sinnbild: 'berechnung',
        hinweis: 'Fallleitungen mit Lüftung nach DIN EN 12056-2',
      },
      {
        schluessel: 'sanitaer-hebeanlagen', pfad: 'sanitaer/hebeanlagen', name: 'Hebeanlagen', sinnbild: 'berechnung',
        hinweis: 'Förderstrom, Druckleitung und Förderhöhe nach DIN EN 12056-4',
      },
      {
        schluessel: 'sanitaer-fettabwasser', pfad: 'sanitaer/fettabwasser', name: 'Fettabwasser', sinnbild: 'berechnung',
        hinweis: 'Fettabscheider nach DIN EN 1825-2 und Vorbehälter',
      },
      {
        schluessel: 'sanitaer-regenwasser', pfad: 'sanitaer/regenwasser', name: 'Regenwasser', sinnbild: 'berechnung',
        hinweis: 'Dachabläufe, Notentwässerung und Regenwasserleitungen nach DIN 1986-100',
      },
      {
        // TS9b2: der Lauf „ueberflutung“ (TS5); PDF gesperrt, bis die Berichtsart SAN-UEB angemeldet ist.
        schluessel: 'sanitaer-ueberflutung', pfad: 'sanitaer/ueberflutung', name: 'Überflutung', sinnbild: 'berechnung',
        hinweis: 'Überflutungsnachweis nach DIN 1986-100 §14.9 mit den KOSTRA-Regenspenden',
      },
    ],
  },
  {
    art: 'daten',
    eintraege: [
      {
        // Index-Route — pfad ist absichtlich der Fachbereichspfad selbst (fällt als Kind == Elternpfad heraus).
        schluessel: 'sanitaer-uebersicht', pfad: 'sanitaer', name: 'Übersicht', sinnbild: 'tabelle',
        hinweis: 'Der Bestand in Kennzahlen: Rohre, Gegenstände, Netze',
      },
      {
        schluessel: 'sanitaer-teilstrecken', pfad: 'sanitaer/teilstrecken', name: 'Teilstrecken', sinnbild: 'tabelle',
        hinweis: 'Jede gerechnete Teilstrecke mit Durchfluss, Geschwindigkeit und Herkunft des Werts',
      },
      {
        // Der Baum: Geschoss -> Raum -> Gegenstandsart -> Gegenstand. Eine Verortung beantwortet „was steht in
        // diesem Raum“, die Übersicht `gegenstaende` „wie viele DU insgesamt“.
        schluessel: 'sanitaer-verortung', pfad: 'sanitaer/verortung', name: 'Verortung', sinnbild: 'raum',
        hinweis: 'Geschoss → Raum → Gegenstandsart → Gegenstand: was steht in diesem Raum',
      },
      {
        schluessel: 'sanitaer-gegenstaende', pfad: 'sanitaer/gegenstaende', name: 'Gegenstände', sinnbild: 'tabelle',
        hinweis: 'Die Etagenübersicht der Sanitärgegenstände: wie viele DU und Vollströme insgesamt',
      },
      {
        schluessel: 'sanitaer-produkte', pfad: 'sanitaer/produkte', name: 'Produktdaten', sinnbild: 'datei',
        hinweis: 'Fabrikate und Kennwerte der Sanitärprodukte',
      },
    ],
  },
  {
    art: 'pruefung',
    eintraege: [
      {
        schluessel: 'sanitaer-befunde', pfad: 'sanitaer/befunde', name: 'Befunde', sinnbild: 'warnung',
        hinweis: 'Offene Rohrenden, Lücken und unbestimmte Einstufungen',
      },
      {
        // TS9b2: je Gegenstand die Sicherungsart - eine Prüfung des Modells gegen den Katalog (SAN-07).
        schluessel: 'sanitaer-absicherung', pfad: 'sanitaer/absicherung', name: 'Absicherung', sinnbild: 'tabelle',
        hinweis: 'Sicherungsarten nach DIN EN 1717 je Gegenstand und die 3-Liter-Regel nach VDI 6023',
      },
    ],
  },
];
